import {Component, Input, OnInit, ViewChild, ElementRef, AfterViewChecked} from '@angular/core';
import {Connect} from '../../services/connect';
import {SharedService} from '../../services/shared';
import {TableInfo} from '../../class/TableInfo';
import {CustomFilter} from '../../class/Filter/CustomFilter';

@Component({
    selector: 'chat-bot',
    templateUrl: './chat-bot.component.html',
    styleUrls: ['./chat-bot.component.scss']
})
export class ChatBotComponent implements OnInit, AfterViewChecked {
    @Input() table_info: TableInfo;
    @Input() customFilter: CustomFilter;
    @Input() table: string;
    @ViewChild('chatBody') private chatBodyRef: ElementRef;

    public isChatOpen: boolean = false;
    public messages: any[] = [];
    public inputMessage: string = '';
    public isLoading: boolean = false;
    private shouldScrollToBottom: boolean = false;
    public lastResponse: any = null; // 最後のレスポンスを保存するプロパティ
    private sessionId: string = null; // 会話セッションIDを保存するプロパティ

    constructor(private _connect: Connect, private _shared: SharedService) {
    }

    ngOnInit(): void {
        // チャット履歴の読み込み
        const savedMessages = localStorage.getItem(`chat_history_${this.table}`);
        if (savedMessages) {
            this.messages = JSON.parse(savedMessages);
        }

        // セッションIDの読み込み（会話の連続性を保つため）
        const savedSessionId = localStorage.getItem(`chat_session_${this.table}`);
        if (savedSessionId) {
            this.sessionId = savedSessionId;
            console.log('Loaded saved session ID:', this.sessionId);
        }
    }

    ngAfterViewChecked(): void {
        if (this.shouldScrollToBottom) {
            this.scrollToBottom();
            this.shouldScrollToBottom = false;
        }
    }

    scrollToBottom(): void {
        try {
            this.chatBodyRef.nativeElement.scrollTop = this.chatBodyRef.nativeElement.scrollHeight;
        } catch (err) {
            console.error('Error scrolling to bottom:', err);
        }
    }

    toggleChat(): void {
        this.isChatOpen = !this.isChatOpen;
        if (this.isChatOpen) {
            setTimeout(() => {
                this.shouldScrollToBottom = true;
            }, 100);
        }
    }

    /**
     * メッセージを送信し、AIからのレスポンスを取得する
     * Send a message and get response from AI
     */
    sendMessage(): void {
        if (!this.inputMessage.trim()) return;

        this.messages.push({
            type: 'user',
            content: this.inputMessage
        });
        this.shouldScrollToBottom = true;

        const filterInfo = this.customFilter ? JSON.stringify(this.customFilter) : '';

        // リクエストデータを作成（セッションIDがある場合は追加）
        const requestData = {
            message: this.inputMessage,
            filter_info: filterInfo,
            table: this.table
        };

        // セッションIDがある場合は追加
        if (this.sessionId) {
            requestData['session_id'] = this.sessionId;
            console.log('Using session ID:', this.sessionId);
        }

        this.inputMessage = '';
        this.isLoading = true;

        this._connect.post(`/admin/${this.table}/chat-by-ai`, requestData).subscribe(
            (response) => {
                this.isLoading = false;

                const botResponse = response.response || {
                    message_html: response.message || '少々お待ちください。。',
                    buttons: []
                };

                this.lastResponse = botResponse; // レスポンスを保存

                // セッションIDを取得する（優先順位：debug_response > response > 既存のセッションID）
                if (response.debug_response && response.debug_response.session_id) {
                    // デバッグレスポンスからセッションIDを取得して保存
                    this.sessionId = response.debug_response.session_id;
                    console.log('Saving session ID from debug response:', this.sessionId);
                } else if (response.session_id) {
                    // 通常のレスポンスからセッションIDを取得して保存
                    this.sessionId = response.session_id;
                    console.log('Saving session ID from response:', this.sessionId);
                }

                // セッションIDをローカルストレージにも保存（会話の継続性を保つため）
                if (this.sessionId) {
                    localStorage.setItem(`chat_session_${this.table}`, this.sessionId);
                }

                this.messages.push({
                    type: 'bot',
                    content: botResponse.message_html || '少々お待ちください。。',
                    buttons: botResponse.buttons || [],
                    checkboxes: botResponse.checkboxes || [],
                    pulldown: botResponse.pulldown || [],
                    session_id: this.sessionId  // メッセージにセッションIDを保存
                });
                this.shouldScrollToBottom = true;

                localStorage.setItem(`chat_history_${this.table}`, JSON.stringify(this.messages));
            },
            (error) => {
                this.isLoading = false;
                console.error('Error sending message:', error);
                this.messages.push({
                    type: 'bot',
                    content: 'エラーが発生しました。しばらく経ってからやり直してください。',
                    buttons: [],
                    checkboxes: [],
                    pulldown: []
                });
                this.shouldScrollToBottom = true;

                localStorage.setItem(`chat_history_${this.table}`, JSON.stringify(this.messages));
            }
        );
    }

    handleKeyUp(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.sendMessage();
        }
    }

    /**
     * ボタンをクリックした時のハンドラー
     * Handler for button clicks
     * @param buttonText ボタンのテキスト
     */
    handleButtonClick(buttonText: string): void {
        this.inputMessage = buttonText;
        this.sendMessage();
    }

    /**
     * チェックボックスの選択状態が変更された時のハンドラー
     * Handler for checkbox changes
     * @param option チェックボックスのオプション
     * @param isChecked 選択状態
     */
    handleCheckboxChange(option: any, isChecked: boolean): void {
    }

    /**
     * プルダウンの選択値が変更された時のハンドラー
     * Handler for pulldown selection changes
     * @param selectedValue 選択された値
     */
    handlePulldownChange(selectedValue: string): void {
        this.inputMessage = selectedValue;
        this.sendMessage();
    }
}
